import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'lognai-primary-color';

/** CSS custom properties driven by the brand primary color. Setting them inline
 *  on <html> overrides the design-system theme defaults (including the
 *  [data-theme="dark"] block) in both light and dark modes. */
const PRIMARY_VARS = [
  '--primary',
  '--ring',
  '--accent',
  '--sidebar-primary',
  '--sidebar-accent',
  '--sidebar-ring',
] as const;

const HEX_RE = /^#?[0-9a-fA-F]{6}$/;

/** Returns a normalized `#rrggbb` string, or null if invalid. */
export function normalizeHex(input: string): string | null {
  const v = input.trim();
  if (!HEX_RE.test(v)) return null;
  return (v.startsWith('#') ? v : `#${v}`).toLowerCase();
}

/** The pre-rebrand default. A stored value equal to it is indistinguishable from
 *  "never customized" — it was what the picker handed back on reset — so it is
 *  treated as unset. Without this, anyone carrying the old blue in localStorage
 *  would keep an inline override on <html> and never see the Heinz navy. */
const LEGACY_DEFAULT = '#2563eb';

function getStored(): string | null {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (!v || !normalizeHex(v)) return null;
    if (normalizeHex(v) === LEGACY_DEFAULT) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return v;
  } catch {
    return null;
  }
}

// Module-level singleton — shared across all hook instances in the same tab.
let currentColor: string | null = getStored();
const listeners = new Set<() => void>();

function apply(color: string | null) {
  const el = document.documentElement;
  if (color) {
    for (const v of PRIMARY_VARS) el.style.setProperty(v, color);
    // Subtle background = the primary at low opacity (e.g. active nav pill).
    el.style.setProperty('--primary-subtle', `${color}1f`);
  } else {
    for (const v of PRIMARY_VARS) el.style.removeProperty(v);
    el.style.removeProperty('--primary-subtle');
  }
}

// Apply on module load so the color is set before first paint.
apply(currentColor);

function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

function getSnapshot() {
  return currentColor;
}

export function setPrimaryColor(hex: string | null) {
  const next = hex ? normalizeHex(hex) : null;
  currentColor = next;
  apply(next);
  try {
    if (next) window.localStorage.setItem(STORAGE_KEY, next);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {}
  listeners.forEach((l) => l());
}

export function usePrimaryColor() {
  const color = useSyncExternalStore(subscribe, getSnapshot);
  return { color, setPrimaryColor };
}
